import type { LandingContent } from "./types";
import heroAshp from "@/assets/air-source-heat-pumps/hero-ashp.jpg.asset.json";
import { rasterSrc } from "@/generated/raster-images";

const ashpCardCylinder = rasterSrc("air-source-heat-pumps/ashp-card-cylinder");
const ashpCardEmitters = rasterSrc("air-source-heat-pumps/ashp-card-emitters");
const ashpCardSiting = rasterSrc("air-source-heat-pumps/ashp-card-siting");
const ashpCardHeatloss = rasterSrc("air-source-heat-pumps/ashp-card-heatloss");
const ashpCardFabric = rasterSrc("air-source-heat-pumps/ashp-card-fabric");
const ashpStep1 = rasterSrc("air-source-heat-pumps/ashp-step-1");
const ashpEvidence = rasterSrc("air-source-heat-pumps/ashp-evidence");

/**
 * Body content for the /air-source-heat-pumps page. Drives the shared
 * LpOrderedSections renderer (trust, testimonials, pricing, FAQ).
 *
 * Page-specific bands (inspect-first, what-we-assess, MCS process,
 * funding-light, fabric-first, on-the-day, social-housing) are inlined
 * in `src/components/landing/lp-air-source-heat-pumps.tsx`.
 *
 * Repositioning rules: no "independent" / "no commission" language; no
 * no finance on this page (finance is solar-only).
 */
export const airSourceHeatPumpsLp: LandingContent = {
  pageKind: "ashp",
  slug: "air-source-heat-pumps",
  path: "/air-source-heat-pumps",
  title: "Air Source Heat Pumps In South Wales",
  metaTitle: "Air Source Heat Pumps South Wales | MCS Certified | CRS Energy",
  metaDescription:
"MCS certified air source heat pump design, install and commissioning across Wales and the South of England. Full heat-loss survey, manufacturer-backed warranty, coordinated retrofit.",
  defaultService: "Air Source Heat Pump",
  heroHeadline: "Air Source Heat Pumps In South Wales",
  heroSubheading:
"Efficient, low-carbon heating across Wales and the South of England, designed and installed by an MCS-certified team. Every system starts with a full heat-loss survey and ends with proper commissioning.",
  heroBullets: [
"MCS Certified",
"Full Heat-Loss Survey",
"Manufacturer-Backed Warranty",
"Free Assessment",
"One Team, Start To Finish",
  ],
  heroImage: heroAshp.url,
  heroAlt: "Air source heat pump outdoor unit installed beside a UK home with neat copper pipework",
  heroFloatImages: [
    { src: heroAshp.url, slug: "ashp-hero" },
    { src: ashpCardCylinder, slug: "ashp-cylinder" },
    { src: ashpCardEmitters, slug: "ashp-emitters" },
    { src: ashpCardSiting, slug: "ashp-siting" },
    { src: ashpCardHeatloss, slug: "ashp-heatloss" },
    { src: ashpCardFabric, slug: "ashp-fabric" },
    { src: ashpStep1, slug: "ashp-step" },
    { src: ashpEvidence, slug: "ashp-evidence" },
  ],
  trustItems: [
"MCS Certified",
"Full Heat-Loss Survey",
"Manufacturer-Backed Warranty",
"Free Assessment",
"One Team, Start To Finish",
  ],

  problemTitle: "Why It Matters",
  problemPoints: [
    {
      title: "How A Heat Pump Works",
      body: "An air source heat pump moves heat from the outside air into your home, delivering it through radiators, underfloor heating and a hot-water cylinder, at a lower running temperature (what installers call flow temperature) than a typical boiler.",
    },
    {
      title: "Design And Sizing Matter",
      body: "A heat pump only performs as well as the system around it. Heat-loss calculation, radiator sizing and cylinder choice are what make or break real-world comfort and efficiency.",

    },
    {
      title: "Insulation First, Heating Second",
      body: "The lower the heat loss, the better any heat pump performs. We look at insulation and ventilation alongside the heating, not in isolation.",
    },

  ],
  clarityTitle: "What A Properly Designed System Looks Like",
  clarityBody:
"A heat-loss calculation room by room, emitters sized to deliver the required output at sensible flow temperatures, a correctly specified cylinder, a tidy outdoor unit siting that respects clearances and neighbours, full commissioning records, and MCS certification on completion.",
  productTitle: "What We Install",
  productPoints: [
    {
      title: "MCS-Certified Air Source Heat Pump",
      body: "Manufacturer-supplied outdoor unit specified to the calculated heat loss, paired with the right controls for the system.",
    },
    {
      title: "Hot Water Cylinder",
      body: "Correctly sized cylinder for the household's hot-water demand, with the right coil for heat pump operation.",
    },
    {
      title: "Radiator Upgrades Where Designed",
      body: "Radiator or underfloor upgrades only where the heat-loss design shows they're needed for sensible running temperatures.",
    },

    {
      title: "Controls & Compensation",
      body: "Heat-pump-compatible controls set up for weather or load compensation so the system runs efficiently.",
    },
  ],
  processSteps: [
    {
      step: 1,
      title: "Survey & Heat-Loss Design",
      description:
"On-site survey, room-by-room heat-loss calculation, emitter assessment and fabric check. This is the work that makes a heat pump perform.",
      icon: "magnifier",
    },
    {
      step: 2,
      title: "Proposal",
      description:
"Written, property-specific proposal: specified heat pump, cylinder, emitter changes, controls, siting and commissioning scope. No guesswork.",
      icon: "file",
    },
    {
      step: 3,
      title: "Install & Commission",
      description:
"MCS-certified install: outdoor unit, cylinder, pipework, electrical, emitter changes, controls and commissioning to manufacturer requirements.",
      icon: "drill",
    },
    {
      step: 4,
      title: "Aftercare & Registration",
      description:
"MCS certificate, manufacturer warranty registration and aftercare guidance handed over on completion.",
      icon: "certificate",
    },
  ],
  qualificationTitle: "What We Assess Before Quoting",
  qualificationItems: [
"Room-by-room heat-loss calculation",
"Existing radiators and underfloor heating",
"Hot-water demand and cylinder requirements",
"Outdoor unit siting and clearances",
"Electrical capacity and incoming supply",
"Insulation and ventilation condition",
  ],
  includedItems: [
"Free pre-design assessment",
"Full heat-loss calculation",
"MCS-certified design and install",
"Specified cylinder and controls",
"Radiator upgrades where designed",
"Commissioning records",
"MCS certificate and manufacturer warranty registration",
  ],
  trustSectionTitle: "Designed, Coordinated, Commissioned",
  trustSectionBody:
"A heat pump isn't a boiler swap. We treat it as a coordinated, fabric-first retrofit measure: design first, install second, commission properly, register the warranty and the MCS certificate on completion.",
  benefits: [
    { title: "Designed System", description: "Heat-loss-led design instead of a guessed swap-out." },
    { title: "Quieter, Steady Heat", description: "Lower, steadier flow temperatures and balanced emitters." },
    { title: "Coordinated Retrofit", description: "Fabric, ventilation and controls considered alongside the heat source." },
    { title: "Documented Install", description: "MCS certificate and manufacturer warranty registered on completion." },
  ],
  testimonialsFull: [
    {
      name: "Frank Ciccotti",
      source: "trustpilot",
      date: "July 2026",
      serviceTag: "Air Source Heat Pump, Solar PV & Insulation",
      quote:
        "Highly competent work, from initial design to installation. We had an air source heat pump, solar panels and extensive insulation installed. Large teams of skilled tradespeople arrived in planned succession. Work was completed within agreed timescales. Problems, where they arose, were quickly resolved. They even painted after completing the work!",
    },
    {
      name: "Ken",
      source: "google",
      date: "May 2024",
      serviceTag: "Heating Upgrade",
      quote:
        "I contacted my local MP Elwyn Vaughn who gave me the name of a company to contact, who then put me in touch with Complete Remedial Solutions in Bridgend. This company got the system up and running today Wednesday 29/05/2024. The manager Rui and the electricians Mike and Mathew who I would recommend to anyone needing a heating upgrade installation, have been absolutely first class in their care and work. I can't thank you enough guys. If I could give more than 5 stars I would.",
    },
    {
      name: "Polly",
      source: "trustpilot",
      date: "September 2023",
      serviceTag: "Air Source Heat Pump & Solar PV",
      quote:
        "Very helpful, friendly and reliable. I used CRS in spring 2023 to do a fairly big retrofit including installing an Air Source Heat Pump, completely new 'wet' central heating system and a small solar panel array. Keith and the team were really helpful and friendly throughout and although there were a few hiccups and delays for various reasons, they kept communicating with me which really helped, until it was all completed. I wouldn't hesitate to use them again.",
    },
  ],
  faqs: [
    {
      question: "Will an air source heat pump actually heat my home properly?",
      answer:
"A correctly designed and commissioned heat pump can heat a typical UK home to comfortable temperatures all year round. The key word is designed. That's why every CRS heat pump starts with a full heat-loss calculation, an emitter assessment and a fabric check before any quote.",
    },
    {
      question: "Do I need new radiators?",
      answer:
"Sometimes a few, sometimes none, occasionally a wider upgrade. We only recommend emitter changes the heat-loss design shows are needed to run at sensible flow temperatures. We'll tell you honestly which radiators are fine and which aren't.",
    },
    {
      question: "Is my home suitable, and does fabric really matter?",
      answer:
"Most homes can take a heat pump, but the lower the heat loss the better the performance. We look at insulation and ventilation alongside the heating so the system isn't fighting an uninsulated fabric. Where fabric improvements would help, we'll explain the options without pressure.",
    },
    {
      question: "How noisy are heat pumps?",
      answer:
"Modern units are quiet, but siting matters. We assess clearances, the distance to neighbours and reflective surfaces during the survey, and select and position the unit accordingly.",
    },
    {
      question: "What about warranty and registration?",
      answer:
"We register the manufacturer warranty on your behalf and provide the MCS certificate on completion. Warranty duration depends on the manufacturer and product chosen, confirmed in your written proposal.",
    },
    {
      question: "Are there grants available?",
      answer:
"Eligible homes may qualify for funded routes such as the Boiler Upgrade Scheme or other current schemes. We'll explain what you may be eligible for and what it would mean for your quote, without making funding the focus.",
    },
  ],
  ctaTitle: "Book Your Free Heat Pump Assessment",
  sectionOrder: ["trust", "testimonials", "pricing", "faq"],
};
